import _ from 'lodash'

// Layout export/import file format. An export carries only what is needed to recreate the layout in
// another workspace: every field is picked by an allowlist below, never "everything except".
// Anything stored on the record that is not listed here (workspace/company ids, who created or
// edited it, claim/share tokens, timestamps, the layout uuid, implementation records and the
// uuids of integrations and flows created from it) is therefore left out automatically, including
// fields added to the record in the future.
// The planned integration and flow uuids are layout-internal ids; an import mints fresh ones.

export const LAYOUT_EXPORT_VERSION = 1

export const INVALID_LAYOUT_FILE_MESSAGE = 'This file is not a layout export we can import. Choose a .json file downloaded with “Export layout”, then try again.'

export const BLANK_NAME_FILE_MESSAGE = 'This layout file has a layout, integration or flow without a name.'

export const BLANK_LAYOUT_EXPORT_MESSAGE = 'Give the layout a name before exporting.'
export const BLANK_ITEM_EXPORT_MESSAGE = 'Name every integration and flow before exporting.'

const SIDE_FIELDS = ['application_id', 'application_label', 'file_type_id', 'file_type_label', 'protocol']

// An empty optional field is stored as null by the backend, so it is exported and imported as null:
// a created record then equals what was sent, and the public planner needs no follow-up update.
const optional = value => value === '' ? null : (value ?? null)

const serializeSide = side => ({
  ...Object.fromEntries(SIDE_FIELDS.map(field => [field, optional(side?.[field])])),
  unknown: Boolean(side?.unknown)
})

const serializeFlow = flow => ({
  flow_type_id: flow.flow_type_id,
  flow_type_label: optional(flow.flow_type_label),
  name: flow.name,
  notes: optional(flow.notes),
  source: serializeSide(flow.source),
  destination: serializeSide(flow.destination)
})

const serializeIntegration = integration => ({
  integration_type_id: integration.integration_type_id,
  integration_type_label: optional(integration.integration_type_label),
  name: integration.name,
  flows: (integration.flows || []).map(serializeFlow)
})

export const serializeLayout = layout => ({
  version: LAYOUT_EXPORT_VERSION,
  name: layout.name,
  integrations: (layout.integrations || []).map(serializeIntegration)
})

export const layoutExportFileName = layout => {
  const slug = _.toLower(layout?.name).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'layout'
  return `${slug}.layout.json`
}

const isBlank = name => !_.isString(name) || name.trim() === ''

// Why the current draft cannot be exported yet, or null when it can: no layout, integration or
// flow may be unnamed. Exports are always taken from the current draft.
export const exportBlocker = layout => {
  if (isBlank(layout?.name)) return BLANK_LAYOUT_EXPORT_MESSAGE
  const integrations = layout.integrations || []
  const unnamed = integrations.some(integration => isBlank(integration.name) || (integration.flows || []).some(flow => isBlank(flow.name)))
  return unnamed ? BLANK_ITEM_EXPORT_MESSAGE : null
}

// Browser download of the export. Kept apart from serializeLayout so that stays pure.
export const downloadLayoutExport = layout => {
  const blob = new Blob([JSON.stringify(serializeLayout(layout), null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = layoutExportFileName(layout)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

const isNonBlankString = value => _.isString(value) && value.trim() !== ''
const isOptionalString = value => _.isNil(value) || _.isString(value)

class InvalidLayoutFile extends Error {}
class BlankNameInLayoutFile extends Error {}

// A missing or blank name gets its own message; any other non-string name is just a bad file.
const assertNamed = name => {
  assertValid(isOptionalString(name))
  if (!isNonBlankString(name)) throw new BlankNameInLayoutFile()
}

const assertValid = condition => {
  if (!condition) throw new InvalidLayoutFile()
}

const parseSide = side => {
  assertValid(_.isNil(side) || _.isPlainObject(side))
  SIDE_FIELDS.forEach(field => assertValid(isOptionalString(side?.[field])))
  assertValid(_.isNil(side?.unknown) || _.isBoolean(side.unknown))
  return serializeSide(side)
}

const parseFlow = (flow, makeUuid) => {
  assertValid(_.isPlainObject(flow))
  assertValid(isNonBlankString(flow.flow_type_id))
  assertNamed(flow.name)
  assertValid(isOptionalString(flow.flow_type_label) && isOptionalString(flow.notes))
  return { flow_uuid: makeUuid(), ...serializeFlow({ ...flow, source: parseSide(flow.source), destination: parseSide(flow.destination) }) }
}

const parseIntegration = (integration, makeUuid) => {
  assertValid(_.isPlainObject(integration))
  assertValid(isNonBlankString(integration.integration_type_id))
  assertNamed(integration.name)
  assertValid(isOptionalString(integration.integration_type_label))
  assertValid(_.isNil(integration.flows) || Array.isArray(integration.flows))
  const integrationUuid = makeUuid()
  const { flows, ...rest } = serializeIntegration({ ...integration, flows: [] })
  return {
    integration_uuid: integrationUuid,
    ...rest,
    flows: (integration.flows || []).map(flow => parseFlow(flow, makeUuid))
  }
}

// Validates the text of an exported file and returns `{ layout }` (the name and integrations a
// layout-create call takes) or `{ error }` with a message safe to show the user. Never throws.
export const parseLayoutImport = (text, makeUuid = () => crypto.randomUUID()) => {
  try {
    const file = JSON.parse(text)
    assertValid(_.isPlainObject(file))
    assertValid(file.version === LAYOUT_EXPORT_VERSION)
    assertNamed(file.name)
    assertValid(Array.isArray(file.integrations))
    return { layout: { name: file.name, integrations: file.integrations.map(integration => parseIntegration(integration, makeUuid)) } }
  } catch (error) {
    return { error: error instanceof BlankNameInLayoutFile ? BLANK_NAME_FILE_MESSAGE : INVALID_LAYOUT_FILE_MESSAGE }
  }
}
